import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, BookOpen } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getPostBySlug } from '../../data/blog-posts';

const categoryColors: Record<string, string> = {
  'Hiring Tips': 'bg-blue-100 text-blue-700',
  'Recruiting': 'bg-purple-100 text-purple-700',
  'Interview Guide': 'bg-green-100 text-green-700',
  'Job Posting': 'bg-orange-100 text-orange-700',
  'HR Technology': 'bg-pink-100 text-pink-700',
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-44 lg:pb-28">
          <div className="container-custom text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">
              The article you are looking for does not exist or has not been published yet.
            </p>
            <Link to="/blog" className="btn-primary">
              Back to Blog
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  categoryColors[post.category] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {post.category}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <Calendar className="w-3 h-3" />
                {new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            <p className="text-lg text-gray-600 leading-relaxed">
              {post.excerpt}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {post.content.map((section, i) => (
              <div key={i} className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.heading}</h2>
                <p className="text-gray-600 leading-relaxed text-base">{section.body}</p>
              </div>
            ))}

            {/* CTA */}
            <div className="mt-16 p-8 bg-primary-50 rounded-2xl text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                <BookOpen className="w-4 h-4" />
                Ready to hire faster?
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Try iHireFast free today
              </h3>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                Create custom application forms, automate candidate emails, and schedule interviews
                with Google Calendar. Free to start, no credit card required.
              </p>
              <a
                href="https://app.ihirefast.com"
                className="btn-primary text-base px-8 py-3 inline-flex items-center gap-2"
              >
                Get Started Free
              </a>
            </div>

            {/* Back link */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
